import { CircularProgress, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  EDITABLE_STATUSES,
  LOSS_TYPE_OPTIONS,
  PAYMENT_MODE_OPTIONS,
  isCashPayment,
  resolveApprovalStatus,
} from "./constants";
import {
  mapGhvTransactions,
  computeCollectionDerived,
  buildDiffOnlySavePayload,
  prefillGhvFromPaymentHistory,
} from "./pureLogic";
import * as api from "./billCollection.service";

const CollectionUpdation = ({
  appointmentId,
  leadId,
  collectionStatus,
  paymentMode,
  // Lens gate — shown above "Collected at Hospital" in the Collectible sub-tab.
  // Locks the sub-tab until the lens choice is resolved.
  isLensApplicable = false,
  hasLensDecision = false,
  lensChoice = "",
  lensSaved = false,
  lensLocked = false,
  onLensChoice,
  onReopenLensModal,
  onStatusChange,
  onApprovalSuccess,
}) => {
  const [loading, setLoading] = useState(true);
  const [collectableAmount, setCollectableAmount] = useState(0);
  const [collectedAtHospital, setCollectedAtHospital] = useState(0);
  const [originalCollectedAtHospital, setOriginalCollectedAtHospital] = useState(0);
  const [ghvTransactions, setGhvTransactions] = useState([]);
  const [extraCashRows, setExtraCashRows] = useState([]);
  const [activeSubTab, setActiveSubTab] = useState("collectible");
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [daDocuments, setDaDocuments] = useState([]);
  const [gapReasons, setGapReasons] = useState([]);
  const [paymentHistoryLoaded, setPaymentHistoryLoaded] = useState(false);
  const [daDocsLoaded, setDaDocsLoaded] = useState(false);
  const [gapReasonsLoaded, setGapReasonsLoaded] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [approveRemarks, setApproveRemarks] = useState("");
  const [lossType, setLossType] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // The status that actually drives the UI. Seeded from the prop, but overridden
  // by the `collectionStatus` returned by get-collection-details — so after a
  // "Send for Approval" save the re-fetch flips this to the new status and the
  // whole form auto-disables (central parity).
  const [effectiveStatus, setEffectiveStatus] = useState(collectionStatus || "");

  // Keep in sync if the parent prop changes (e.g. appointment switch) — but the
  // API response is the source of truth once it arrives.
  useEffect(() => {
    setEffectiveStatus(collectionStatus || "");
  }, [collectionStatus]);

  const isFormDisabled = effectiveStatus && !EDITABLE_STATUSES.includes(effectiveStatus);
  const isCollectibleZero = collectableAmount === 0;
  const isCash = isCashPayment(paymentMode);

  const { totalGhvCollected, collectionGapRaw, hasExcess, excessAmount, hasLoss } =
    computeCollectionDerived(
      collectableAmount,
      collectedAtHospital,
      ghvTransactions.map((t) => t.amount)
    );

  const fetchDetails = useCallback(async () => {
    if (!appointmentId) return;
    setLoading(true);
    try {
      const res = await api.getCollectionDetails(appointmentId);
      const d = res?.data || res || {};
      setCollectableAmount(d.collectableAmount || 0);
      setCollectedAtHospital(d.collectedAtHospital || 0);
      setOriginalCollectedAtHospital(d.collectedAtHospital || 0);
      setExtraCashRows(d.extraCashProof || []);
      // API status is the source of truth — drives disabling after save (central parity).
      if (d.collectionStatus) {
        setEffectiveStatus(d.collectionStatus);
        onStatusChange?.(d.collectionStatus);
      }
      const mappedGhv = mapGhvTransactions(d.collectedAtGHV);
      // Prefill GHV rows from success payment-history records (central parity).
      //
      // ORDERING MATTERS: the collection-details GHV (mappedGhv) is the base and
      // must be computed BEFORE the payment-history merge, then written to state
      // in a SINGLE atomic setGhvTransactions. Never fire these two reads in
      // parallel — if collection-details resolves last it would overwrite the
      // merged success rows and "Collected at GHV" would show empty (this was the
      // race that broke the central dashboard; here it's sequenced so rows bind).
      try {
        const phRes = await api.getPaymentHistory(appointmentId);
        const phRows = phRes?.result || phRes?.data || [];
        setGhvTransactions(prefillGhvFromPaymentHistory(mappedGhv, phRows));
      } catch {
        setGhvTransactions(mappedGhv);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load collection details");
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const loadPaymentHistory = async () => {
    if (paymentHistoryLoaded) return;
    try {
      const res = await api.getPaymentHistory(appointmentId);
      // API returns rows under `result` (central parity); fall back to `data`.
      setPaymentHistory(res?.result || res?.data || []);
    } catch {
      toast.error("Failed to load payment history");
    } finally {
      setPaymentHistoryLoaded(true);
    }
  };

  const loadDaDocs = async () => {
    if (daDocsLoaded) return;
    try {
      const res = await api.getDaDocuments(leadId);
      setDaDocuments(res?.data || []);
    } catch {
      toast.error("Failed to load DA documents");
    } finally {
      setDaDocsLoaded(true);
    }
  };

  const loadGapReasons = async () => {
    if (gapReasonsLoaded) return;
    try {
      const res = await api.getCollectionGapReason(appointmentId);
      setGapReasons(Array.isArray(res?.data) ? res.data : res?.data ? [res.data] : []);
    } catch {
      toast.error("Failed to load gap reasons");
    } finally {
      setGapReasonsLoaded(true);
    }
  };

  const addGhvRow = () => {
    setGhvTransactions((prev) => [
      ...prev,
      { amount: "", paymentMode: "UPI", transactionId: "" },
    ]);
  };

  const updateGhvRow = (index, field, value) => {
    setGhvTransactions((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const removeGhvRow = (index) => {
    setGhvTransactions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleApprove = async () => {
    const status = resolveApprovalStatus(hasLoss, isCash);
    const collectionLoss = hasLoss ? collectionGapRaw : 0;
    setSubmitting(true);
    try {
      const payload = buildDiffOnlySavePayload(
        {
          appointmentId,
          status,
          remarks: approveRemarks,
          lossType,
          collectionLoss,
          collectedAtHospital,
          ghvTransactions,
        },
        { collectedAtHospital: originalCollectedAtHospital }
      );
      await api.saveCollectionEntry(payload);
      toast.success("Collection entry saved");
      setApproveModalOpen(false);
      fetchDetails();
      // Only a successful Send for Approval closes the parent modal/accordion.
      onApprovalSuccess?.();
    } catch {
      toast.error("Failed to save collection entry");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="cbc-loading">
        <CircularProgress size={24} />
      </div>
    );
  }

  return (
    <div>
      {/* Heading + Collection Status box (parity with central dashboard) */}
      <div className="cu-head">
        <div className="cu-head-title">COLLECTION UPDATION</div>
        <div className="cu-status-box">
          <span className="cu-status-label">Collection Status:</span>
          <span className="cu-status-pill">{effectiveStatus || "N/A"}</span>
        </div>
      </div>

      {/* Info banner when collectible is zero */}
      {isCollectibleZero && (
        <div className="cu-info-banner">
          <span className="cu-info-icon">&#9432;</span>
          Bill Approve Pending At Finance.
        </div>
      )}

      {/* Sub-tabs */}
      <div className="cbc-tabs" style={{ marginBottom: 12 }}>
        {[
          { key: "collectible", label: "Collectible Amount" },
          { key: "extraCash", label: "Extra Cash Proof" },
          { key: "paymentHistory", label: "Payment History" },
          { key: "daDocs", label: "DA Document" },
          { key: "gapRemarks", label: "Collection Gap Remarks" },
        ].map(({ key, label }) => (
          <button
            key={key}
            className={`cbc-tab ${activeSubTab === key ? "active" : ""}`}
            onClick={() => {
              setActiveSubTab(key);
              if (key === "paymentHistory") loadPaymentHistory();
              if (key === "daDocs") loadDaDocs();
              if (key === "gapRemarks") loadGapReasons();
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Collectible Amount tab */}
      {activeSubTab === "collectible" && (
        <div>
          <div className="ca-wrap">
            {/* Left green summary card */}
            <div className="ca-summary-card">
              <div className="ca-sum-block">
                <div className="ca-sum-label">COLLECTIBLE AMOUNT</div>
                <div className="ca-sum-value">&#8377; {collectableAmount || 0}</div>
              </div>
              <div className="ca-sum-block">
                <div className="ca-sum-label">HOSPITAL</div>
                <div className="ca-sum-value-sm">&#8377;{collectedAtHospital || 0}</div>
              </div>
              <div className="ca-sum-block">
                <div className="ca-sum-label">GHV</div>
                <div className="ca-sum-value-sm">&#8377;{totalGhvCollected || 0}</div>
              </div>
              <div className="ca-sum-block ca-gap">
                <div className="ca-sum-label">COLLECTION GAP</div>
                <div className="ca-sum-value-sm">
                  &#8377;{Math.max(collectionGapRaw, 0)}
                </div>
              </div>
              {lossType && (
                <div className="ca-sum-block">
                  <div className="ca-sum-label">LOSS TYPE</div>
                  <span className="ca-loss-badge">{lossType}</span>
                </div>
              )}
              {hasExcess && (
                <div className="ca-sum-block ca-excess">
                  <div className="ca-sum-label">EXCESS AMOUNT</div>
                  <div className="ca-sum-value-sm ca-excess-value">
                    &#8377;{excessAmount}
                  </div>
                </div>
              )}
            </div>

            {/* Right collection entry */}
            <div className="ca-entry">
              {/* Lens gate — only when the final bill flags lens as applicable
                  AND no decision has been persisted yet. Shown ABOVE "Collected
                  at Hospital"; the sub-tab stays locked until resolved. */}
              {isLensApplicable && !hasLensDecision && (
                <div className="cbc-lens-gate">
                  <div className="cbc-lens-note">
                    Lens is required for this hospital
                  </div>
                  <div className="cbc-lens-options">
                    <label className="cbc-lens-option">
                      <input
                        type="radio"
                        name="caLensChoice"
                        checked={lensChoice === "Applicable"}
                        onChange={() => onLensChoice?.("Applicable")}
                      />
                      <span>Applicable</span>
                    </label>
                    <label className="cbc-lens-option">
                      <input
                        type="radio"
                        name="caLensChoice"
                        checked={lensChoice === "Not Applicable"}
                        onChange={() => onLensChoice?.("Not Applicable")}
                      />
                      <span>Not Applicable</span>
                    </label>
                  </div>
                  {lensChoice === "Applicable" && (
                    <div className="cbc-lens-status">
                      {lensSaved ? (
                        <span className="cbc-lens-ok">Lens details saved.</span>
                      ) : (
                        <span className="cbc-lens-warn">
                          Lens details not saved yet.{" "}
                          <button
                            type="button"
                            className="cbc-lens-reopen"
                            onClick={() => onReopenLensModal?.()}
                          >
                            Enter Lens Details
                          </button>
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="ca-entry-row">
                <label className="ca-entry-label">Collected at Hospital:</label>
                <input
                  type="number"
                  className="cbc-input ca-hospital-input"
                  value={collectedAtHospital}
                  onChange={(e) => setCollectedAtHospital(Number(e.target.value) || 0)}
                  disabled={isFormDisabled || isCollectibleZero || lensLocked}
                />
              </div>

              <div className="ca-ghv-head">
                <span className="ca-ghv-title">Collected at GHV:</span>
                <button
                  type="button"
                  className="ca-add-btn"
                  onClick={addGhvRow}
                  disabled={isFormDisabled || isCollectibleZero || lensLocked}
                >
                  + Add Another Transaction
                </button>
              </div>

              <div className="cbc-table-wrap">
                <table className="ca-ghv-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Amount (Rs.)</th>
                      <th>Payment Mode</th>
                      <th>Transaction ID</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ghvTransactions.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="ca-empty">
                          No GHV transactions added.
                        </td>
                      </tr>
                    ) : (
                      ghvTransactions.map((t, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td>
                            <input
                              type="number"
                              className="cbc-input"
                              value={t.amount}
                              onChange={(e) => updateGhvRow(i, "amount", e.target.value)}
                              disabled={isFormDisabled || isCollectibleZero || lensLocked}
                            />
                          </td>
                          <td>
                            <select
                              className="cbc-select"
                              value={t.paymentMode}
                              onChange={(e) => updateGhvRow(i, "paymentMode", e.target.value)}
                              disabled={isFormDisabled || isCollectibleZero || lensLocked}
                            >
                              {PAYMENT_MODE_OPTIONS.map((pm) => (
                                <option key={pm} value={pm}>{pm}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              className="cbc-input"
                              value={t.transactionId}
                              onChange={(e) => updateGhvRow(i, "transactionId", e.target.value)}
                              disabled={isFormDisabled || isCollectibleZero || lensLocked}
                            />
                          </td>
                          <td className="ca-action-cell">
                            {!t.id && (
                              <button
                                type="button"
                                className="ca-remove-btn"
                                onClick={() => removeGhvRow(i)}
                                title="Remove"
                                disabled={isFormDisabled || lensLocked}
                              >
                                &#10006;
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="ca-ghv-total">
                Total GHV Collected: &#8377; {totalGhvCollected || 0}
              </div>
            </div>
          </div>

        </div>
      )}

      {/* Extra Cash Proof tab */}
      {activeSubTab === "extraCash" && (
        <div>
          {extraCashRows.length === 0 ? (
            <div className="cbc-empty">No extra cash proof records</div>
          ) : (
            <div className="cbc-table-wrap">
              <table className="cbc-table">
                <thead>
                  <tr>
                    <th>Amount</th>
                    <th>At Time Of</th>
                    <th>Collected By</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {extraCashRows.map((row, i) => (
                    <tr key={i}>
                      <td>{row.amount}</td>
                      <td>{row.atTheTimeOf}</td>
                      <td>{row.collectedBy}</td>
                      <td>
                        <span className={`cbc-pill ${row.status === "Approved" ? "green" : "yellow"}`}>
                          {row.status || "N/A"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Payment History tab */}
      {activeSubTab === "paymentHistory" && (
        <div>
          {!paymentHistoryLoaded ? (
            <div className="cbc-loading"><CircularProgress size={20} /></div>
          ) : paymentHistory.length === 0 ? (
            <div className="cbc-empty">No payment history</div>
          ) : (
            <div className="cbc-table-wrap">
              <table className="cbc-table">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Invoice</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map((row) => (
                    <tr key={row._id}>
                      <td>{row.vendor}</td>
                      <td>₹{row.amount}</td>
                      <td>
                        <span className={`cbc-pill ${row.status?.toLowerCase() === "success" ? "green" : "yellow"}`}>
                          {row.status}
                        </span>
                      </td>
                      <td>{row.invoiceNumber || "-"}</td>
                      <td>{row._created_at ? new Date(row._created_at).toLocaleDateString("en-IN") : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* DA Document tab */}
      {activeSubTab === "daDocs" && (
        <div>
          {!daDocsLoaded ? (
            <div className="cbc-loading"><CircularProgress size={20} /></div>
          ) : daDocuments.length === 0 ? (
            <div className="cbc-empty">No DA documents</div>
          ) : (
            <div className="cbc-table-wrap">
              <table className="cbc-table">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th>Loan Amount</th>
                    <th>EMI Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {daDocuments.map((row, i) => (
                    <tr key={i}>
                      <td>{row.vendor}</td>
                      <td>₹{row.loanAmount}</td>
                      <td>{row.emiStatus}</td>
                      <td>{row.date || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Gap Remarks tab */}
      {activeSubTab === "gapRemarks" && (
        <div>
          {!gapReasonsLoaded ? (
            <div className="cbc-loading"><CircularProgress size={20} /></div>
          ) : gapReasons.length === 0 ? (
            <div className="cbc-empty">No gap remarks</div>
          ) : (
            gapReasons.map((r, i) => (
              <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid #f3f4f6" }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{r.value}</div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                  {r.actionTakenByUser} — {r.dateTimeIso ? new Date(r.dateTimeIso).toLocaleString("en-IN") : ""}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Send for Approval — always rendered (central parity), disabled when
          form is locked, collectible is zero, or a save is in flight. */}
      <div className="cu-actions" style={{ marginTop: 16, textAlign: "right" }}>
        <button
          type="button"
          className="cbc-btn primary full-mobile"
          onClick={() => setApproveModalOpen(true)}
          disabled={submitting || isFormDisabled || isCollectibleZero || lensLocked}
        >
          Send for Approval
        </button>
      </div>

      {/* Approve Dialog */}
      <Dialog open={approveModalOpen} onClose={() => setApproveModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #eef0f4" }}>
          <span className="ca-modal-title">Send for Approval</span>
          <IconButton onClick={() => setApproveModalOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "20px !important" }}>
          {/* Note — 3 variants: loss / reconcile / excess (central parity) */}
          {hasLoss && (
            <p className="ca-note">
              The collected amount does not match the collectible. Mark the collection
              gap as a loss to proceed.
            </p>
          )}
          {!hasLoss && !hasExcess && (
            <p className="ca-note">
              Collections reconcile exactly. You can send this collection for approval.
            </p>
          )}
          {hasExcess && (
            <p className="ca-note">
              Collected amount exceeds collectible. Excess amount will be recorded.
            </p>
          )}

          {/* Summary box */}
          <div className="ca-summary">
            <div className="ca-row">
              <span>Collectible Amount</span>
              <span>&#8377; {collectableAmount || 0}</span>
            </div>
            <div className="ca-row">
              <span>Collected at Hospital</span>
              <span>&#8377; {Number(collectedAtHospital) || 0}</span>
            </div>
            <div className="ca-row">
              <span>Collected at GHV</span>
              <span>&#8377; {totalGhvCollected || 0}</span>
            </div>
            <div className="ca-row ca-row-total">
              <span>Collection Gap (Loss)</span>
              <span>&#8377; {Math.max(collectionGapRaw, 0)}</span>
            </div>
            {/* Loss Type badge (once selected) */}
            {lossType && (
              <div className="ca-row ca-row-sub">
                <span className="ca-loss-label">Loss Type</span>
                <span className="ca-loss-badge">{lossType}</span>
              </div>
            )}
            {/* Excess Amount row */}
            {hasExcess && (
              <div className="ca-row ca-row-excess">
                <span>Excess Amount</span>
                <span className="ca-excess-value">&#8377; {excessAmount}</span>
              </div>
            )}
          </div>

          {/* Loss type dropdown — only when there is a loss */}
          {hasLoss && (
            <div className="ca-field">
              <label className="ca-label">
                MARK &#8377; {Math.max(collectionGapRaw, 0)} AS COLLECTION LOSS?
              </label>
              <label className="ca-label">
                Collection Loss Type <span className="req">*</span>
              </label>
              <select
                className="cbc-select"
                value={lossType}
                onChange={(e) => setLossType(e.target.value)}
              >
                <option value="">-- Select Loss Type --</option>
                {LOSS_TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          )}

          {/* Remarks — required on loss, optional otherwise */}
          <div className="ca-field">
            <label className="ca-label">
              Remarks {hasLoss && <span className="req">*</span>}
            </label>
            <textarea
              className="cbc-input"
              rows={3}
              value={approveRemarks}
              onChange={(e) => setApproveRemarks(e.target.value)}
              placeholder={hasLoss ? "Enter reason for the collection loss." : "Enter remarks (optional)."}
              style={{ resize: "vertical", minHeight: 80 }}
            />
          </div>

          {/* Footer — centered purple Send for Approval */}
          <div style={{ display: "flex", justifyContent: "center", borderTop: "1px solid #eef0f4", paddingTop: 16, marginTop: 4 }}>
            <button
              className="ca-submit"
              onClick={handleApprove}
              disabled={submitting || (hasLoss && (!lossType || !approveRemarks.trim()))}
            >
              {submitting ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                <>Send for Approval &#10003;</>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CollectionUpdation;
